/**
 * Application chrome for the merchant dashboard.
 *
 * The layout is driven by the device the merchant actually uses: an iPad propped
 * on a counter gets a persistent left rail, because tapping between panels must
 * not cost a scroll; a phone gets a bottom tab bar, because a thumb cannot reach
 * the top of the screen.
 *
 * The rail collapses to icons. On a 1194pt landscape iPad that returns 160pt of
 * horizontal space to the order grid, which is worth a control that remembers its
 * state. Collapsed items keep their labels as hover tooltips and as
 * accessibility labels, so icon-only mode is a visual change rather than a loss
 * of information.
 *
 * The pending-order count lives in the rail rather than only on the orders
 * screen, so a merchant editing the menu can see the lunch rush arrive.
 */

import type { ReactNode } from 'react';
import { Pressable, ScrollView, View } from 'react-native';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ChefHat,
  Clock,
  Gauge,
  LayoutDashboard,
  LogOut,
  PanelLeftClose,
  PanelLeftOpen,
  QrCode,
  ReceiptText,
  Settings,
  UtensilsCrossed,
  type LucideIcon,
} from 'lucide-react-native';
import { cookBatchCount, cookRoundSeconds, type MessageKey } from '@yummy/shared';
import {
  Divider,
  Icon,
  QuickPreferences,
  Row,
  Spacer,
  Stack,
  Text,
  ToneBadge,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  cn,
  usePersistentState,
  useResponsive,
  useT,
} from '@yummy/ui-rnr';

import { useMerchant } from '../app/auth';
import { useOrders, type ConnectionStatus } from '../app/orders';
import { SHELL_HEADER_HEIGHT } from './shell-metrics';

interface NavItem {
  path: string;
  /** Dictionary key, not a label: the rail renders in the active locale. */
  labelKey: MessageKey;
  icon: LucideIcon;
  /** Matches nested routes such as `/menu/:id`. */
  prefix?: boolean;
}

const NAV: NavItem[] = [
  { path: '/', labelKey: 'merchant.nav.dashboard', icon: LayoutDashboard },
  { path: '/orders', labelKey: 'merchant.nav.orders', icon: ReceiptText, prefix: true },
  { path: '/cook', labelKey: 'merchant.nav.cook', icon: ChefHat },
  { path: '/menu', labelKey: 'merchant.nav.menu', icon: UtensilsCrossed, prefix: true },
  { path: '/hours', labelKey: 'merchant.nav.hours', icon: Clock },
  { path: '/capacity', labelKey: 'merchant.nav.capacity', icon: Gauge },
  { path: '/tables', labelKey: 'merchant.nav.tables', icon: QrCode, prefix: true },
  { path: '/settings', labelKey: 'merchant.nav.settings', icon: Settings },
];

/** The status dot and its label, per connection state. */
const CONNECTION_DOT: Record<ConnectionStatus, string> = {
  live: 'bg-success',
  connecting: 'bg-subtle-foreground',
  offline: 'bg-warning',
};

const CONNECTION_TEXT: Record<ConnectionStatus, string> = {
  live: 'text-success',
  connecting: 'text-subtle-foreground',
  offline: 'text-warning',
};

/**
 * Renders the merchant chrome around the active screen.
 *
 * @param props The routed screen to place inside the chrome.
 * @returns The rail on a tablet, or the header and tab bar on a phone.
 */
export function MerchantShell({ children }: { children: ReactNode }) {
  const t = useT();
  const navigate = useNavigate();
  const location = useLocation();
  const { isTablet } = useResponsive();
  const { restaurant, restaurants, selectRestaurant, logout } = useMerchant();
  const { queue, connection } = useOrders();

  const [collapsed, setCollapsed] = usePersistentState('yummy.merchant.rail.collapsed', false);

  const isActive = (item: NavItem): boolean =>
    item.path === '/'
      ? location.pathname === '/'
      : item.prefix
        ? location.pathname.startsWith(item.path)
        : location.pathname === item.path;

  const pendingCount = queue.filter((o) => o.status === 'pending').length;
  const activeCount = queue.length;
  // The kitchen's badge counts batches and the Orders badge counts orders: one is the
  // cook's unit and the other is the counter's. They are different jobs and the two
  // numbers are not comparable, so neither is folded into the other. The count comes
  // from the same function the cook page's own badge reads, so the rail cannot point at
  // a page showing a different number — and it counts only orders the counter has
  // accepted, because an unaccepted order is not work yet.
  //
  // The window is the shop's own, read from the same field the cook page reads —
  // and the default until its settings arrive, so the two cannot count apart.
  const cookCount = cookBatchCount(queue, undefined, cookRoundSeconds(restaurant));

  const badgeFor = (item: NavItem): number | undefined => {
    if (item.path === '/orders') return activeCount > 0 ? activeCount : undefined;
    if (item.path === '/cook') return cookCount > 0 ? cookCount : undefined;
    return undefined;
  };

  // ---------------------------------------------------------------------------
  // Phone layout: sticky header plus a bottom tab bar
  // ---------------------------------------------------------------------------

  if (!isTablet) {
    return (
      <View className="bg-background flex-1">
        <View className="bg-card border-border border-b px-4 pt-4 pb-3">
          <Row spread gap="md" align="center">
            <Stack gap="xs" className="min-w-0 flex-1">
              <Text numberOfLines={1} className="text-xl font-semibold">
                {restaurant?.name ?? t('common.appName')}
              </Text>
              <ConnectionLabel connection={connection} pending={pendingCount} />
            </Stack>
            <QuickPreferences />
            {/* The phone layout has no sidebar, so its footer — where the rail
                keeps sign-out — is never rendered. Without this there is no way
                out of the app on a phone at all. */}
            <Pressable
              onPress={() => void logout()}
              accessibilityLabel={t('merchant.nav.signOut')}
              className="active:opacity-70">
              <View className="border-border h-9 w-9 items-center justify-center rounded-full border">
                <Icon as={LogOut} size={16} aria-hidden className="text-muted-foreground" />
              </View>
            </Pressable>
          </Row>
        </View>

        <View className="min-h-0 flex-1">{children}</View>

        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          // `grow-0` is load-bearing: React Native Web's ScrollView grows by
          // default, so without it the tab bar takes the free space the content
          // above it should have, and the tabs float mid-page with the rest of
          // the height empty below them.
          className="grow-0 bg-card border-border border-t"
          contentContainerClassName="gap-2 px-3 py-2">
          {NAV.map((item) => {
            const active = isActive(item);
            const badge = badgeFor(item);
            const label = t(item.labelKey);
            return (
              <Pressable
                key={item.path}
                onPress={() => navigate(item.path)}
                accessibilityRole="button"
                accessibilityLabel={label}
                className="active:opacity-70">
                <View
                  className={cn(
                    'min-w-[68px] items-center gap-1 rounded-md px-3 py-2',
                    active ? 'bg-primary-soft' : 'bg-transparent'
                  )}>
                  <Icon
                    as={item.icon}
                    size={20}
                    aria-hidden
                    className={cn(active ? 'text-primary-strong' : 'text-muted-foreground')}
                  />
                  <Row gap="xs" align="center">
                    <Text
                      className={cn(
                        'text-[11px] font-semibold',
                        active ? 'text-primary-strong' : 'text-muted-foreground'
                      )}>
                      {label}
                    </Text>
                    {badge ? (
                      <ToneBadge label={String(badge)} tone="brand" emphasis="solid" size="sm" />
                    ) : null}
                  </Row>
                </View>
              </Pressable>
            );
          })}
        </ScrollView>
      </View>
    );
  }

  // ---------------------------------------------------------------------------
  // Tablet layout: persistent left rail, collapsible to icons
  // ---------------------------------------------------------------------------

  return (
    <View className="bg-background flex-1 flex-row">
      <View
        className={cn(
          'bg-card border-border shrink-0 border-r pb-4',
          // The difference between the two widths is the space collapsing
          // returns to the order grid.
          collapsed ? 'w-[76px]' : 'w-[236px]'
        )}>
        <Stack gap="lg" className="flex-1">
          {/* Wordmark and collapse control ---------------------------------- */}
          {/* Collapsed, this row is the rail's half of the top rule: it ends at
              the same height as a screen's pinned header, so the two borders
              meet as one line instead of stopping a few pixels apart. Expanded
              it is taller than the header by its nature, so it draws no border
              and carries its own padding. */}
          <Row
            gap="sm"
            align="center"
            justify={collapsed ? 'center' : 'between'}
            className={cn(
              !collapsed && 'px-4 pt-4',
              collapsed && cn('border-border border-b', SHELL_HEADER_HEIGHT)
            )}>
            {collapsed ? null : (
              <Stack gap="xs" className="min-w-0 flex-1">
                <Text className="text-primary text-2xl font-bold">{t('common.appName')}</Text>
                <Text className="text-subtle-foreground text-xs">
                  {t('merchant.nav.merchant')}
                </Text>
              </Stack>
            )}

            <Tooltip>
              <TooltipTrigger
                onPress={() => setCollapsed(!collapsed)}
                accessibilityLabel={
                  collapsed ? t('merchant.nav.expand') : t('merchant.nav.collapse')
                }>
                <View className="bg-muted h-9 w-9 items-center justify-center rounded-md">
                  <Icon
                    as={collapsed ? PanelLeftOpen : PanelLeftClose}
                    size={18}
                    aria-hidden
                    className="text-muted-foreground"
                  />
                </View>
              </TooltipTrigger>
              <TooltipContent side="right">
                <Text>{collapsed ? t('merchant.nav.expand') : t('merchant.nav.collapse')}</Text>
              </TooltipContent>
            </Tooltip>
          </Row>

          {/* Restaurant switcher -------------------------------------------- */}
          {collapsed ? null : (
            <View className="px-3">
              {restaurants.length > 1 ? (
                <Stack gap="xs">
                  <Text className="text-subtle-foreground px-2 text-[11px]">
                    {t('merchant.nav.restaurant').toUpperCase()}
                  </Text>
                  {restaurants.map((option) => {
                    const selected = option.id === restaurant?.id;
                    return (
                      <Pressable
                        key={option.id}
                        onPress={() => selectRestaurant(option.id)}
                        accessibilityRole="button"
                        accessibilityLabel={option.name}
                        className="active:opacity-70">
                        <View
                          className={cn(
                            'rounded-sm px-2 py-2',
                            selected ? 'bg-primary-soft' : 'bg-transparent'
                          )}>
                          <Text
                            numberOfLines={1}
                            className={cn(
                              'text-sm',
                              selected
                                ? 'text-primary-strong font-bold'
                                : 'text-muted-foreground font-medium'
                            )}>
                            {option.name}
                          </Text>
                        </View>
                      </Pressable>
                    );
                  })}
                </Stack>
              ) : (
                <Stack gap="xs" className="px-2">
                  <Text className="text-subtle-foreground text-[11px]">
                    {t('merchant.nav.restaurant').toUpperCase()}
                  </Text>
                  <Text numberOfLines={2} className="text-lg font-semibold">
                    {restaurant?.name ?? '—'}
                  </Text>
                </Stack>
              )}
            </View>
          )}

          {/* The divider separates the identity block from the nav. Collapsed,
              the identity row's own bottom border already does that. */}
          {collapsed ? null : <Divider />}

          {/* Navigation ------------------------------------------------------ */}
          <Stack gap="xs" className={cn(collapsed ? 'px-2' : 'px-3')}>
            {NAV.map((item) => {
              const active = isActive(item);
              const badge = badgeFor(item);
              const label = t(item.labelKey);

              const row = (
                <View
                  className={cn(
                    'min-h-12 flex-row items-center rounded-md',
                    collapsed ? 'justify-center' : 'gap-3 px-3',
                    active ? 'bg-primary-soft' : 'bg-transparent'
                  )}>
                  <Icon
                    as={item.icon}
                    size={20}
                    aria-hidden
                    className={cn(active ? 'text-primary-strong' : 'text-muted-foreground')}
                  />
                  {collapsed ? null : (
                    <Text
                      numberOfLines={1}
                      className={cn(
                        'flex-1 text-base',
                        active
                          ? 'text-primary-strong font-bold'
                          : 'text-muted-foreground font-medium'
                      )}>
                      {label}
                    </Text>
                  )}
                  {/* The count survives collapsing: it is the one thing here that
                      a merchant must not miss. */}
                  {badge ? (
                    <View className={cn(collapsed && 'absolute top-1.5 right-2')}>
                      <ToneBadge label={String(badge)} tone="brand" emphasis="solid" size="sm" />
                    </View>
                  ) : null}
                </View>
              );

              return collapsed ? (
                <Tooltip key={item.path}>
                  <TooltipTrigger
                    onPress={() => navigate(item.path)}
                    accessibilityLabel={label}>
                    {row}
                  </TooltipTrigger>
                  <TooltipContent side="right">
                    <Text>{label}</Text>
                  </TooltipContent>
                </Tooltip>
              ) : (
                <Pressable
                  key={item.path}
                  onPress={() => navigate(item.path)}
                  accessibilityRole="button"
                  accessibilityLabel={label}
                  className="active:opacity-70">
                  {row}
                </Pressable>
              );
            })}
          </Stack>

          <Spacer />

          {/* Footer ---------------------------------------------------------- */}
          <Stack gap="md" className={cn(collapsed ? 'px-2' : 'px-4')}>
            {collapsed ? null : <ConnectionLabel connection={connection} pending={pendingCount} />}

            <Row justify={collapsed ? 'center' : 'start'}>
              {/* The collapsed rail is 76 points wide, so the two pills stack
                  rather than sitting side by side and spilling into the grid. */}
              <QuickPreferences orientation={collapsed ? 'vertical' : 'horizontal'} />
            </Row>

            <Tooltip>
              <TooltipTrigger
                onPress={() => void logout()}
                accessibilityLabel={t('merchant.nav.signOut')}>
                <View
                  className={cn(
                    'min-h-9 flex-row items-center justify-center gap-2 rounded-md py-2',
                    collapsed ? 'min-w-12' : 'px-3'
                  )}>
                  <Icon as={LogOut} size={16} aria-hidden className="text-muted-foreground" />
                  {collapsed ? null : (
                    <Text numberOfLines={1} className="text-muted-foreground text-sm font-semibold">
                      {t('merchant.nav.signOut')}
                    </Text>
                  )}
                </View>
              </TooltipTrigger>
              <TooltipContent side="top">
                <Text>{t('merchant.nav.signOut')}</Text>
              </TooltipContent>
            </Tooltip>
          </Stack>
        </Stack>
      </View>

      <View className="min-w-0 flex-1">{children}</View>
    </View>
  );
}

/**
 * Reports websocket health and unacknowledged order count.
 *
 * @param props Current connection state and pending count.
 * @returns The status dot, its label, and the new-order count when there is one.
 */
function ConnectionLabel({
  connection,
  pending,
}: {
  connection: ConnectionStatus;
  pending: number;
}) {
  const t = useT();

  return (
    <Row gap="sm" align="center">
      <View className={cn('h-2 w-2 rounded-full', CONNECTION_DOT[connection])} />
      <Text className={cn('text-[11px]', CONNECTION_TEXT[connection])}>
        {t(`merchant.connection.${connection}`)}
      </Text>
      {pending > 0 ? (
        <Text className="text-primary-strong text-[11px] font-bold">
          {t('merchant.connection.newCount', { count: pending })}
        </Text>
      ) : null}
    </Row>
  );
}
